import { StringIndexedObject } from '@kanda-libs/ks-design-library';
import {
  FinanceProvider,
  FinanceRate,
  FinanceType,
  WorkType,
} from '@kanda-libs/ks-frontend-services';
import { map } from 'fp-ts/Array';
import { groupBy } from 'fp-ts/NonEmptyArray';
import { flow, pipe } from 'fp-ts/lib/function';
import { pluralise } from './String-functions';

export type GroupedByKey = Record<string, FinanceRate[]>;

export type GroupedByAprAndType = Record<string, FinanceRate[]>;

export interface FinanceRateDetails {
  key: string;
  apr: number;
  duration: number;
  financeTypes: FinanceType[];
}

export type FinanceOptionDetails = {
  interest: number;
  duration: number;
};

export type CombinedRates = StringIndexedObject<
  StringIndexedObject<FinanceRate[]>
>;

export const FINANCE_PROVIDER_PRIORITY_ORDER: FinanceProvider[] = [
  'allium',
  'propensio',
  'humm',
  'etika',
  'omni',
  'novuna',
  'demo',
];

export const filterForRatesByWorkType = (
  rates: FinanceRate[],
  workType: WorkType,
): FinanceRate[] =>
  rates.filter((rate) => {
    if ((rate?.work_types || []).length === 0) return true;
    return !workType || !rate.work_types || rate.work_types.includes(workType);
  });

export const filterForPrimaryRates = (rates: FinanceRate[]): FinanceRate[] =>
  rates.filter((rate) => rate.finance_types.includes('primary'));

export const filterForEnabledRates = (rates: FinanceRate[]): FinanceRate[] =>
  rates.filter((rate) => rate.enabled);

export const rateIsUnregulated = (rate: FinanceRate) =>
  rate.apr_type === 'INTEREST_FREE' && (rate?.duration || 12) <= 10;

export const rateIsBuyNowPayLater = (rate: FinanceRate) =>
  rate.apr_type === 'BUYNOW_PAYLATER';

export const filterForUnregulated = (
  rates: FinanceRate[],
  unregulated = false,
): FinanceRate[] =>
  rates.filter((rate) => {
    const isUnregulated = rateIsUnregulated(rate);
    return unregulated ? isUnregulated : !isUnregulated;
  });

export const filterForValidRates = (
  rates: FinanceRate[],
  workType: WorkType,
  unregulated = false,
): FinanceRate[] =>
  pipe(
    rates,
    // Only return enabled rates
    (currentRates) => filterForEnabledRates(currentRates),
    // Only return rates that are available for primary finance
    filterForPrimaryRates,
    (currentRates) => filterForRatesByWorkType(currentRates, workType),
    // Filter for rates depending on the buyNowPayLater flag
    (currentRates) => filterForUnregulated(currentRates, unregulated),
  );

export const rateIsInterestFree = (rate: FinanceRate) =>
  rate.name.includes('_FREE');

export const getFinanceRateInterestRate = (rate: FinanceRate): number => {
  if (rateIsInterestFree(rate)) return 0;
  if (rate?.apr) return rate.apr;
  return Number(rate.name.split('_').pop());
};

export const getFinanceRateDuration = (rate: FinanceRate): number => {
  if (rate?.duration) return rate.duration;
  return Number(rate.name.split('_', 3).pop());
};

export const getFinanceRateKey = (rate: FinanceRate): string => {
  const interestRate = getFinanceRateInterestRate(rate);
  const duration = getFinanceRateDuration(rate);
  const { provider, fee, charge } = rate;
  return [interestRate, duration, provider, fee, charge].join('-');
};

export const getFinanceRateDetails = (
  rate: FinanceRate,
): FinanceRateDetails => ({
  key: getFinanceRateKey(rate),
  apr: getFinanceRateInterestRate(rate),
  duration: getFinanceRateDuration(rate),
  financeTypes: rate.finance_types,
});

export const getFinanceRateGroupName = (
  { apr, financeTypes }: FinanceRateDetails,
  indictateSecondLine = false,
  suppressInterestLabel = false,
): string => {
  if (indictateSecondLine && financeTypes.includes('secondary'))
    return `${apr / 100}% (2nd line)`;
  return `${apr / 100}% APR${suppressInterestLabel ? '' : ' interest'}`;
};

export const groupByInterestRate = (
  rates: FinanceRate[],
  indictateSecondLine = false,
  suppressInterestLabel = false,
): StringIndexedObject<FinanceRate[]> =>
  pipe(
    rates,
    // Group the rates by the group name (interest rate) & provider
    groupBy(
      flow(getFinanceRateDetails, (details) =>
        getFinanceRateGroupName(
          details,
          indictateSecondLine,
          suppressInterestLabel,
        ),
      ),
    ),
  );

const financeRateIsWithinJobTotalBounds = (
  financeRate: FinanceRate,
  jobTotal: number,
): boolean => {
  if (!financeRate.min_job_value && !financeRate.max_job_value) return true;
  return (
    jobTotal >= ((financeRate.min_job_value as number) || 0) &&
    jobTotal <= (financeRate.max_job_value as number)
  );
};

const financeRateIsWithinDepositPercentageBounds = (
  financeRate: FinanceRate,
  depositPercentage: number,
): boolean => {
  if (!financeRate.min_deposit_pct && !financeRate.max_deposit_pct) return true;

  // Deposit percentage is stored as a pence value to avoid
  // issues with floating point numbers
  const formattedDepositPercentage = depositPercentage * 100;

  return (
    formattedDepositPercentage >= 0 &&
    formattedDepositPercentage <= (financeRate.max_deposit_pct as number)
  );
};

const financeRateIsWithinDepositAmountBounds = (
  financeRate: FinanceRate,
  depositAmount: number,
): boolean => {
  if (!financeRate.min_deposit_value && !financeRate.max_deposit_value)
    return true;

  return (
    depositAmount >= ((financeRate.min_deposit_value as number) || 0) &&
    depositAmount <= (financeRate.max_deposit_value as number)
  );
};

const financeRateIsWithinDepositBounds = (
  financeRate: FinanceRate,
  depositPercentage: number,
  depositAmount: number,
): boolean =>
  financeRateIsWithinDepositPercentageBounds(financeRate, depositPercentage) &&
  financeRateIsWithinDepositAmountBounds(financeRate, depositAmount);

export const financeRateIsWithinBounds = (
  financeRate: FinanceRate,
  jobTotal: number,
  depositPercentage: number,
  depositAmount: number,
): boolean => {
  const withinJobTotalBounds = financeRateIsWithinJobTotalBounds(
    financeRate,
    jobTotal,
  );

  const withinDepositBounds = financeRateIsWithinDepositBounds(
    financeRate,
    depositPercentage,
    depositAmount,
  );

  if (!withinJobTotalBounds) return false;
  if (!withinDepositBounds) return false;

  return true;
};

const sortInterestRateGroupByRateWithinBounds = (
  group: StringIndexedObject<FinanceRate[]>,
  total: number,
  depositPercentage: number,
  depositAmount: number,
): StringIndexedObject<FinanceRate[]> => {
  const keys = Object.keys(group);

  return keys.reduce(
    (rates, key) => ({
      ...rates,
      [key]: [...(group[key] || [])].sort((i, j) => {
        const iWithinBounds = financeRateIsWithinBounds(
          i,
          total,
          depositPercentage,
          depositAmount,
        )
          ? 1
          : 0;
        const jWithinBounds = financeRateIsWithinBounds(
          j,
          total,
          depositPercentage,
          depositAmount,
        )
          ? 1
          : 0;

        return jWithinBounds - iWithinBounds;
      }),
    }),
    {} as StringIndexedObject<FinanceRate[]>,
  );
};

export const groupValidRatesByInterestRate = (
  rates: FinanceRate[],
  total: number,
  workType: WorkType,
  unregulated = false,
  depositPercentage = 0,
  depositAmount = 0,
) =>
  pipe(
    rates,
    (currentRates) => filterForValidRates(currentRates, workType, unregulated),
    groupByInterestRate,
    (currentRates) =>
      sortInterestRateGroupByRateWithinBounds(
        currentRates,
        total,
        depositPercentage,
        depositAmount,
      ),
  );

export const getSortedFinanceRateKeys = (
  rates: StringIndexedObject<FinanceRate[]>,
): string[] =>
  Object.keys(rates).sort(
    (i, j) => Number(i.split('%').shift()) - Number(j.split('%').shift()),
  );

const getMinimumDuration = (financeRates: FinanceRate[]): number =>
  [...(financeRates || [])]
    .map(({ duration }) => duration || 0)
    .sort()
    .shift() || 0;

const sortFinanceRateChargeAndProviderKeys = (
  group: StringIndexedObject<FinanceRate[]>,
): StringIndexedObject<FinanceRate[]> => {
  const keys = Object.keys(group);
  const sortedKeys = keys.sort((i, j) => {
    const minimumI = getMinimumDuration(group[i]);
    const minimumJ = getMinimumDuration(group[j]);

    return minimumI - minimumJ;
  });

  return sortedKeys.reduce(
    (rates, key) => ({
      ...rates,
      [key]: group[key],
    }),
    {} as StringIndexedObject<FinanceRate[]>,
  );
};

export const getFinanceRateFeeChargeProviderBnplKey = (
  rate: FinanceRate,
): string =>
  [
    rate.fee,
    rate.charge,
    rate.provider,
    rate?.deferred_duration ? 'bnpl' : undefined,
  ]
    .filter(Boolean)
    .join('-');

export const groupByFeeChargeProviderBnpl = (
  rates: FinanceRate[],
): StringIndexedObject<FinanceRate[]> =>
  pipe(
    rates,
    groupBy(getFinanceRateFeeChargeProviderBnplKey),
    sortFinanceRateChargeAndProviderKeys,
  );

export const findCombinedRates = (
  rates: StringIndexedObject<FinanceRate[]>,
): CombinedRates =>
  getSortedFinanceRateKeys(rates).reduce(
    (formattedRates, rateKey) => ({
      ...formattedRates,
      [rateKey]: groupByFeeChargeProviderBnpl(rates[rateKey]),
    }),
    {} as CombinedRates,
  );

export const getBuyNowPayLaterRateLabel = (
  rate: FinanceRate,
  hasPrefix = false,
): string =>
  [
    hasPrefix ? 'BNPL' : 'BNPL',
    [rate.deferred_duration, rate.duration].join(' + '),
    'months',
  ]
    .filter(Boolean)
    .join(' ');

export const getFinanceOptionDetails = (
  financeOption: FinanceRate,
): FinanceOptionDetails => {
  const { name } = financeOption;
  const splitName = name?.split('_');
  if (name.toLowerCase().includes('free'))
    return {
      interest: 0,
      duration: parseInt(splitName.slice(-1)[0], 10),
    };
  const [durationString, interestString] = splitName.slice(-2);
  return {
    interest: parseInt(interestString, 10) / 100,
    duration: parseInt(durationString, 10),
  };
};

const eitherUnitShouldUseMonths = (min: number, max: number | null) =>
  min < 12 || (max !== null && max < 12);

const eitherUnitYearIsNotWholeNumber = (min: number, max: number | null) =>
  min % 12 !== 0 || (max !== null && max % 12 !== 0);

export const getLabelUnit = (
  minMonths: number,
  maxMonths: number | null,
): 'month' | 'year' => {
  if (eitherUnitShouldUseMonths(minMonths, maxMonths)) return 'month';
  if (eitherUnitYearIsNotWholeNumber(minMonths, maxMonths)) return 'month';
  return 'year';
};

export const getFormattedUnit = (
  value: number,
  unit: 'month' | 'year',
): number => (unit === 'month' ? value : value / 12);

export const getLabelValue = (min: number, max: number): string =>
  min === max ? min.toString() : [min, max].filter(Boolean).join('-');

export const getLabel = ([min, max]: [number, number | null]): string =>
  pipe(
    [min, max] as [number, number | null],
    ([currentMin, currentMax]) =>
      [currentMin, currentMax, getLabelUnit(currentMin, currentMax)] as [
        number,
        number,
        'month' | 'year',
      ],
    ([currentMin, currentMax, unit]) =>
      [
        getFormattedUnit(currentMin, unit),
        getFormattedUnit(currentMax, unit),
        unit,
      ] as [number, number, 'month' | 'year'],
    ([currentMin, currentMax, unit]) => [
      getLabelValue(currentMin, currentMax),
      pluralise(currentMin, unit, `${unit}s`),
    ],
    (value) => value.join(' '),
  );

export const getFinanceRateLabel = (rate: FinanceRate): string =>
  pipe(
    rate,
    (currentRate) =>
      [currentRate, rateIsBuyNowPayLater(rate)] as [FinanceRate, boolean],
    ([currentRate, isBuyNowPayLater]) => {
      if (isBuyNowPayLater) return getBuyNowPayLaterRateLabel(currentRate);
      const { duration } = getFinanceOptionDetails(currentRate);
      return getLabel([duration, null]);
    },
  );

export const getCombinedFinanceRatesDurations = map(getFinanceRateDuration);

export const getDurationMinMax = (durations: number[]): [number, number] =>
  pipe(durations, (value) => [Math.min(...value), Math.max(...value)]);

export const getBnplLabel = (rates: FinanceRate[], label: string): string => {
  if (!rates.some((rate) => rate?.apr_type === 'BUYNOW_PAYLATER')) return label;
  return `BNPL ${label}`;
};

export const getCombinedFinanceRatesDurationLabel = (
  rates: FinanceRate[],
): string =>
  pipe(
    rates,
    getCombinedFinanceRatesDurations,
    getDurationMinMax,
    getLabel,
    (label) => getBnplLabel(rates, label),
  );

interface RawFinanceRateDetails {
  apr: string;
}

export const getRawRateDetails = (
  rate: FinanceRate,
): RawFinanceRateDetails => ({
  apr: rate?.apr ? String(rate.apr) : 'unknown',
});

export const groupByRawApr = (
  rates: FinanceRate[],
): StringIndexedObject<FinanceRate[]> =>
  pipe(rates, groupBy(flow(getRawRateDetails, (details) => details.apr)));

export const getPrimaryRates = (rates: FinanceRate[]): FinanceRate[] =>
  rates.filter((rate: FinanceRate) => rate.finance_types.includes('primary'));

export const groupByAprAndType = (rates: FinanceRate[]): GroupedByAprAndType =>
  rates.reduce((final: GroupedByAprAndType, current: FinanceRate) => {
    const { apr, apr_type: aprType } = current;
    if (apr === undefined || aprType === undefined) return final;
    const key = `${String(apr)}-${aprType}`;
    if (Object.keys(final).includes(key))
      return {
        ...final,
        [key]: [...final[key], current],
      };
    return {
      ...final,
      [key]: [current],
    };
  }, {});

export const groupByDuration = (rates: FinanceRate[]): GroupedByKey =>
  rates.reduce((final: GroupedByKey, current: FinanceRate) => {
    const rawKey = current?.duration;
    if (rawKey === undefined) return final;
    const key = String(rawKey);
    if (Object.keys(final).includes(key))
      return {
        ...final,
        [key]: [...final[key], current],
      };
    return {
      ...final,
      [key]: [current],
    };
  }, {});

export const extractPriorityRateByLender = (
  rates: FinanceRate[],
): FinanceRate =>
  rates.sort(
    (rate1, rate2) =>
      FINANCE_PROVIDER_PRIORITY_ORDER.indexOf(rate1.provider) -
      FINANCE_PROVIDER_PRIORITY_ORDER.indexOf(rate2.provider),
  )[0];
